// jsCAT Question Selector — IRT-based adaptive question selection
// Phase 1: Core implementation using @bdelab/jscat
// Multi-cert: all CAT instances and SE snapshots are keyed by certId

import { Cat } from '@bdelab/jscat';
import { certPath, read, write } from './firebase-store.js';

// Per-cert, per-domain CAT instances: { [certId]: { [domain]: Cat } }
const _catInstances = {};

// SE snapshots for delta computation: { [certId]: { [label]: { [domain]: { theta, se } } } }
const _seSnapshots = {};

/**
 * Initialize a CAT instance for a specific cert + domain.
 *
 * @param {string} certId
 * @param {number} domain - 1-4
 * @param {number} initialTheta - starting ability estimate (default 0.0)
 * @returns {Cat} jsCAT Cat instance
 */
export function initCat(certId, domain, initialTheta = 0.0) {
  // Clamp initialTheta to [-4, 4] to prevent degenerate priors
  const clampedTheta = Math.max(-4, Math.min(4, Number.isFinite(initialTheta) ? initialTheta : 0.0));
  if (!_catInstances[certId]) _catInstances[certId] = {};
  _catInstances[certId][domain] = new Cat({
    method: 'EAP',
    itemSelect: 'MFI',
    theta: clampedTheta,
    minTheta: -4,
    maxTheta: 4,
    priorDist: 'norm',
    priorPar: [clampedTheta, 1]  // Center prior on saved theta
  });
  return _catInstances[certId][domain];
}

/**
 * Get or create CAT instance for a cert + domain.
 * @param {string} certId
 * @param {number} domain
 */
export function getCat(certId, domain) {
  if (!_catInstances[certId]) _catInstances[certId] = {};
  if (!_catInstances[certId][domain]) {
    initCat(certId, domain);
  }
  return _catInstances[certId][domain];
}

/**
 * Select the optimal next question using Maximum Fisher Information.
 *
 * @param {object} cat - jsCAT Cat instance
 * @param {Array} candidatePool - array of question objects with irtDifficulty, irtDiscrimination
 * @returns {string|null} questionId of the optimal question, or null if pool is empty
 */
export function selectQuestion(cat, candidatePool) {
  if (!candidatePool || candidatePool.length === 0) return null;

  // Map to jsCAT stimulus format (a, b, c, d) with clamped IRT params
  const stimuli = candidatePool.map(q => ({
    a: Math.max(0.1, Math.min(3.0, Number.isFinite(q.irtDiscrimination) ? q.irtDiscrimination : 1.0)),
    b: Math.max(-4, Math.min(4, Number.isFinite(q.irtDifficulty) ? q.irtDifficulty : 0.0)),
    c: 0,                            // guessing (not modeled)
    d: 1,                            // slipping (not modeled)
    _qid: q.questionId || q.id       // preserve original ID
  }));

  const { nextStimulus } = cat.findNextItem(stimuli, 'MFI');
  return nextStimulus ? nextStimulus._qid : null;
}

/**
 * Update ability estimate after an answer.
 *
 * @param {object} cat - jsCAT Cat instance
 * @param {object} questionIrtParams - { irtDifficulty, irtDiscrimination }
 * @param {boolean} correct
 * @returns {object} the updated Cat instance
 */
export function updateAbility(cat, questionIrtParams, correct) {
  const zeta = {
    a: questionIrtParams.irtDiscrimination || 1.0,
    b: questionIrtParams.irtDifficulty || 0.0,
    c: 0,
    d: 1
  };
  cat.updateAbilityEstimate(zeta, correct ? 1 : 0);
  return cat;
}

/**
 * Get current theta (ability estimate) for a cert + domain.
 * @param {string} certId
 * @param {number|object} domainOrCat
 */
export function getTheta(certId, domainOrCat) {
  if (typeof domainOrCat === 'number') {
    return getCat(certId, domainOrCat).theta;
  }
  return domainOrCat.theta;
}

/**
 * Get standard error of measurement for a cert + domain.
 * @param {string} certId
 * @param {number|object} domainOrCat
 */
export function getSE(certId, domainOrCat) {
  if (typeof domainOrCat === 'number') {
    return getCat(certId, domainOrCat).seMeasurement;
  }
  return domainOrCat.seMeasurement;
}

/**
 * Build candidate pool for question selection.
 * Stateless — receives data, no cert-scoping needed.
 *
 * @param {object} params
 * @param {Array} params.questionBank - all questions with metadata
 * @param {Array} params.dueCards - cards currently due (from FSRS)
 * @param {object} params.sessionConstraints - { domainFocus, newVsReviewRatio, phase }
 * @param {Set} params.seenThisSession - set of questionIds already seen
 * @returns {Array} filtered candidate pool
 */
export function buildCandidatePool({ questionBank, dueCards, sessionConstraints, seenThisSession }) {
  const seen = seenThisSession || new Set();
  const dueSet = new Set((dueCards || []).map(c => c.questionId));
  const phase = sessionConstraints?.phase || 'core';
  const domainFocus = sessionConstraints?.domainFocus || null;
  const newRatio = Math.max(0, Math.min(1, sessionConstraints?.newVsReviewRatio ?? 0.3));

  let candidates = questionBank.filter(q => {
    // Exclude already seen this session
    if (seen.has(q.id)) return false;
    // Filter placeholder questions (no valid answer)
    if (q.ans === '?') return false;
    // Domain filter
    if (domainFocus && q.domain !== domainFocus) return false;
    return true;
  });

  if (phase === 'warmup') {
    const warmupDue = candidates.filter(q => dueSet.has(q.id));
    const warmupEasy = candidates.filter(q => !dueSet.has(q.id) && (q.complexity == null || q.complexity <= 1));
    const warmupPool = warmupDue.length > 0 ? warmupDue : warmupEasy.length > 0 ? warmupEasy : candidates;
    warmupPool.sort((a, b) => (a.irtDifficulty || 0) - (b.irtDifficulty || 0));
    return warmupPool.slice(0, 20);
  }

  if (phase === 'consolidation') {
    const consolPool = candidates.filter(q => q.complexity == null || q.complexity <= 2);
    const finalConsol = consolPool.length > 0 ? consolPool : candidates;
    finalConsol.sort((a, b) => (a.irtDifficulty || 0) - (b.irtDifficulty || 0));
    return finalConsol.slice(0, 15);
  }

  // Core phase: full adaptive selection
  const due = candidates.filter(q => dueSet.has(q.id));
  const fresh = candidates.filter(q => !dueSet.has(q.id));

  let pool;
  if (due.length === 0) {
    pool = fresh;
  } else if (fresh.length === 0) {
    pool = due;
  } else {
    const targetDue = Math.ceil(candidates.length * (1 - newRatio));
    const targetNew = Math.ceil(candidates.length * newRatio);
    pool = [
      ...due.slice(0, targetDue),
      ...fresh.slice(0, targetNew)
    ];
  }

  return pool.length > 0 ? pool : candidates.slice(0, 10);
}

/**
 * Get all CAT instance states for a specific cert.
 * @param {string} certId - if provided, returns states for that cert only
 * @returns {object} { [domain]: { theta, se, itemCount } }
 */
export function getAllCatStates(certId) {
  if (certId) {
    const certCats = _catInstances[certId] || {};
    const states = {};
    for (const [domain, cat] of Object.entries(certCats)) {
      states[domain] = {
        theta: cat.theta,
        se: cat.seMeasurement,
        itemCount: cat.nItems || 0
      };
    }
    return states;
  }

  // Return all certs' states
  const allStates = {};
  for (const [cid, certCats] of Object.entries(_catInstances)) {
    allStates[cid] = {};
    for (const [domain, cat] of Object.entries(certCats)) {
      allStates[cid][domain] = {
        theta: cat.theta,
        se: cat.seMeasurement,
        itemCount: cat.nItems || 0
      };
    }
  }
  return allStates;
}

/**
 * Persist theta estimates to Firebase (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 */
export async function saveTheta(userId, certId) {
  const certCats = _catInstances[certId] || {};
  const theta = {};
  for (const [domain, cat] of Object.entries(certCats)) {
    theta[domain] = { theta: cat.theta, se: cat.seMeasurement };
  }
  await write(certPath(userId, certId, 'profile/theta'), theta);
}

/**
 * Capture a snapshot of SE (standard error) per domain for a cert.
 * Call at session start and end to compute uncertainty reduction.
 * @param {string} certId
 * @param {string} label - 'start' or 'end'
 * @returns {object} { [domain]: { theta, se } }
 */
export function captureSessionSE(certId, label) {
  if (!_seSnapshots[certId]) _seSnapshots[certId] = {};
  const certCats = _catInstances[certId] || {};
  const snapshot = {};
  for (const [domain, cat] of Object.entries(certCats)) {
    snapshot[domain] = {
      theta: cat.theta,
      se: cat.seMeasurement
    };
  }
  _seSnapshots[certId][label] = snapshot;
  return snapshot;
}

/**
 * Compute SE delta between start and end snapshots for a cert.
 * Negative deltaSE = uncertainty reduced (good).
 * @param {string} certId
 * @returns {object|null} { [domain]: { startSE, endSE, deltaSE, startTheta, endTheta, deltaTheta } }
 */
export function computeSEDelta(certId) {
  const certSnapshots = _seSnapshots[certId];
  if (!certSnapshots) return null;
  const start = certSnapshots['start'];
  const end = certSnapshots['end'];
  if (!start || !end) return null;

  const result = {};
  const allDomains = new Set([...Object.keys(start), ...Object.keys(end)]);

  for (const d of allDomains) {
    const s = start[d] || { theta: 0, se: 1 };
    const e = end[d] || { theta: 0, se: 1 };
    result[d] = {
      startSE: Math.round(s.se * 1000) / 1000,
      endSE: Math.round(e.se * 1000) / 1000,
      deltaSE: Math.round((e.se - s.se) * 1000) / 1000,
      startTheta: Math.round(s.theta * 1000) / 1000,
      endTheta: Math.round(e.theta * 1000) / 1000,
      deltaTheta: Math.round((e.theta - s.theta) * 1000) / 1000
    };
  }
  return result;
}

/**
 * Load theta estimates from Firebase and restore CAT instances for a cert.
 * @param {string} userId
 * @param {string} certId
 */
export async function loadTheta(userId, certId) {
  const theta = await read(certPath(userId, certId, 'profile/theta'));
  if (!theta) return;

  for (const [domain, data] of Object.entries(theta)) {
    initCat(certId, parseInt(domain) || domain, data.theta || 0.0);
  }
}
